import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  Input,
  Switch,
} from '@wove/web-ui'
import { CargowiseHandlingMethod } from '@wove/api'

const handlingMethodSchema = z.object({
  cargowiseCode: z.string().min(1, 'Handling method code is required'),
  name: z.string().min(1, 'Handling method name is required'),
  isActive: z.boolean(),
})

type FormData = z.infer<typeof handlingMethodSchema>

interface CargowiseHandlingMethodFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  handlingMethod?: CargowiseHandlingMethod | null
  onSubmit: (data: FormData) => Promise<void>
  isSubmitting?: boolean
}

export default function CargowiseHandlingMethodFormDialog({
  open,
  onOpenChange,
  handlingMethod,
  onSubmit,
  isSubmitting = false,
}: CargowiseHandlingMethodFormDialogProps) {
  const isEditing = !!handlingMethod

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(handlingMethodSchema),
    defaultValues: {
      cargowiseCode: '',
      name: '',
      isActive: true,
    },
  })

  // Reset form when handling method changes or dialog opens
  useEffect(() => {
    if (open) {
      if (handlingMethod) {
        reset({
          cargowiseCode: handlingMethod.cargowiseCode,
          name: handlingMethod.name,
          isActive: handlingMethod.isActive,
        })
      } else {
        reset({
          cargowiseCode: '',
          name: '',
          isActive: true,
        })
      }
    }
  }, [open, handlingMethod, reset])

  const handleFormSubmit = async (data: FormData) => {
    await onSubmit(data)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Edit Handling Method' : 'Create Handling Method'}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update the handling method details below.'
                : 'Enter the details for the new CargoWise handling method.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="code">Handling Method Code</Label>
              <Input
                id="code"
                {...register('cargowiseCode')}
                placeholder="e.g., FCL"
                disabled={isEditing}
              />
              {errors.cargowiseCode && (
                <p className="text-sm text-red-600">{errors.cargowiseCode.message}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="name">Handling Method Name</Label>
              <Input id="name" {...register('name')} placeholder="e.g., Full Container Load" />
              {errors.name && <p className="text-sm text-red-600">{errors.name.message}</p>}
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="active">Active</Label>
              <Switch
                id="active"
                checked={watch('isActive')}
                onCheckedChange={(checked) => setValue('isActive', checked)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? isEditing
                  ? 'Updating...'
                  : 'Creating...'
                : isEditing
                ? 'Update'
                : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
